import React, { forwardRef } from 'react';
import { InlineError } from './InlineError';
import { HelpText } from './HelpText';
import styles from './input.module.scss';

export type TextFieldProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'prefix' | 'suffix'
> & {
  label?: React.ReactNode;
  helpText?: React.ReactNode;
  error?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    {
      label,
      id,
      helpText,
      error,
      prefix,
      suffix,
      style,
      className,
      required,
      ...rest
    },
    ref
  ) {
    const inputId = id ?? rest.name ?? undefined;
    const helpId = helpText ? `${inputId}-help` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;

    return (
      <label
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          minWidth: 0
        }}
      >
        {label && (
          <span
            style={{
              fontWeight: 'var(--font-weight-bold)',
              fontSize: 'var(--font-size-base)',
              lineHeight: '1.5rem',
              letterSpacing: '0.01em',
              color: 'var(--color-text-primary, #1a1a1a)'
            }}
          >
            {label}
            {required && (
              <span
                style={{
                  color: 'var(--color-error, #dc2626)',
                  marginLeft: '0.25rem'
                }}
                aria-hidden="true"
              >
                *
              </span>
            )}
          </span>
        )}
        <div className={`${className ?? ''} ${styles.container}`} style={style}>
          {prefix && (
            <span style={{ color: 'var(--color-text-secondary, #6b7280)' }}>
              {prefix}
            </span>
          )}
          <input
            {...rest}
            id={inputId}
            ref={ref}
            required={required}
            className={styles.input}
            aria-describedby={
              [helpId, errorId].filter(Boolean).join(' ') || undefined
            }
            aria-invalid={Boolean(error)}
          />
          {suffix && (
            <span style={{ color: 'var(--color-text-secondary, #6b7280)' }}>
              {suffix}
            </span>
          )}
        </div>
        {helpText && <HelpText id={helpId}>{helpText}</HelpText>}
        {error && <InlineError id={errorId}>{error}</InlineError>}
      </label>
    );
  }
);
